import { createTheme, darken, lighten } from "@mui/material/styles";


const defaultTheme = createTheme();
const augmentColor = defaultTheme.palette.augmentColor;

export const MAIN_COLOR = "#f25d3f";
export const SECONDARY_COLOR = "#FFD700";

function createAppTheme() {
  return createTheme({
    cssVariables: {
      colorSchemeSelector: "class",
    },
    colorSchemes: {
      light: {
        palette: {
          primary: {
            main: MAIN_COLOR,
            light: lighten(MAIN_COLOR, 0.2),
            dark: darken(MAIN_COLOR, 0.2),
          },
          secondary: {
            main: SECONDARY_COLOR,
            light: lighten(SECONDARY_COLOR, 0.2),
            dark: darken(SECONDARY_COLOR, 0.2),
          },
          success: {
            main: "#38B2AC", // Calmer teal
          },
          error: {
            main: "#E53E3E", // Vivid red
          },
          warning: {
            main: "#FBBF24", // Brighter amber
          },
          background: {
            default: "#F7FAFC", // Soft off-white
            paper: "#FFFFFF",
          },
          text: {
            primary: "#212121",
            secondary: "#757575",
          },
          divider: "#BDBDBD",
          neutral: augmentColor({
            color: {
              main: "#393e44",
            },
            name: "neutral",
          }),
          yoto: augmentColor({
            color: {
              main: "#ff5722",
            },
            name: "yoto",
          }),
        },
      },
      dark: {
        palette: {
          primary: {
            main: MAIN_COLOR,
            light: lighten(MAIN_COLOR, 0.2),
            dark: darken(MAIN_COLOR, 0.2),
          },
          secondary: {
            main: SECONDARY_COLOR,
            light: lighten(SECONDARY_COLOR, 0.2),
            dark: darken(SECONDARY_COLOR, 0.2),
          },
          success: {
            main: "#38B2AC", // Calmer teal
          },
          error: {
            main: "#E53E3E", // Vivid red
          },
          warning: {
            main: "#FBBF24", // Brighter amber
          },
          background: {
            default: "#121212",
            paper: "#1e1e1e",
          },
          text: {
            primary: "#ffffff",
            secondary: "#b0b0b0",
          },
          divider: "#BDBDBD",
          neutral: augmentColor({
            color: {
              main: "#dee5ec",
            },
            name: "neutral",
          }),
          yoto: augmentColor({
            color: {
              main: "#ff5722",
            },
            name: "yoto",
          }),
        },
      },
    },
    typography: {
      fontFamily: 'var(--font-body), "Helvetica", "Arial", sans-serif',
      h1: {
        fontFamily: "var(--font-headline), serif",
      },
      h2: {
        fontFamily: "var(--font-headline), serif",
      },
      h3: {
        fontFamily: "var(--font-headline), serif",
      },
      h4: {
        fontFamily: "var(--font-headline), serif",
      },
      h5: {
        fontFamily: "var(--font-headline), serif",
      },
      body1: {
        fontSize: "1.2rem",
      },
      body2: {
        fontSize: "1rem",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          @property --borderRotationAngle {
            syntax: '<angle>';
            initial-value: 0deg;
            inherits: false;
          }
          @keyframes borderRotation {
            to {
              --borderRotationAngle: 1turn;
            }
          }
        `,
      },
      MuiTableContainer: {
        defaultProps: {
          sx: {
            bgcolor: "background.paper",
          },
        },
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }),
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 6,
            borderTopLeftRadius: "6px",
            borderTopRightRadius: "6px",
          },
          root: ({ theme }) => ({
            borderBottom: `1px solid ${theme.palette.divider}`,
            marginBottom: "16px",
          }),
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            minWidth: 0,
            letterSpacing: 0.5,
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
            marginLeft: theme.spacing(1),
            marginRight: theme.spacing(1),
            overflow: "unset",
            fontWeight: 500,
            textTransform: "none",
            "&::before": {
              content: '""',
              position: "absolute",
              left: "-1rem",
              right: "-1rem",
              height: "100%",
            },
            "@media (hover: hover)": {
              "&:hover::before": {
                backgroundColor: theme.palette.action.hover,
              },
            },
            "&.Mui-focusVisible": {
              "&::before": {
                outline: "1px solid currentColor",
                outlineOffset: "-1px",
                backgroundColor: theme.palette.action.hover,
              },
            },
            [theme.breakpoints.up("md")]: {
              minWidth: 0,
            },
          }),
        },
      },
      MuiSelect: {
        variants: [
          {
            props: { variant: "standard" },
            style: {
              '&:before': {
                borderBottom: 'none',
              },
              '&:after': {
                borderBottom: 'none',
              },
              '&:hover:not(.Mui-disabled):before': {
                borderBottom: 'none',
              },
            },
          },
        ],
      },
      MuiFab: {
        styleOverrides: {
          root: {
            textTransform: "none",
          },
        },
      },
      MuiButtonGroup: {
        variants: [
          {
            props: { variant: "audio" },
            style: {
              border: "1px solid",
              color: defaultTheme.palette.common.white,
              backgroundColor: "rgba(255,255,255,0)",
              borderColor: "rgba(255,255,255,0)",
              borderRadius: "16px",
              height: "46px",
              minWidth: "46px",
              "&:hover": {
                boxShadow: "none",
              },
            },
          },
        ],
      },
      MuiButton: {
        variants: [
          {
            props: { variant: "audio" },
            style: {
              border: "1px solid",
              color: defaultTheme.palette.common.white,
              backgroundColor: "rgba(255,255,255,0.05)",
              borderColor: "rgba(255,255,255,0.07)",
              borderRadius: "16px",
              padding: "6px 12px",
              height: "46px",
              minWidth: "46px",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.14)",
                boxShadow: "none",
              },
            },
          },
          {
            props: { variant: "snake" },
            style: {
              position: "relative",
              backgroundColor: "transparent",
              boxShadow: "none",
              borderWidth: 4,
              borderColor: "var(--mui-palette-primary-main)",
              borderStyle: "solid",
              color: "var(--mui-palette-primary-main)",
              transition: "none",
              "&:hover": {
                borderColor: "transparent",
                fontWeight: 600,
              },
              "&::before": {
                position: "absolute",
                inset: 0,
                zIndex: -1,
                padding: "6px",
                borderRadius: "2px",
                background: "transparent",
                filter: "url(#glow)",
                animation: "borderRotation 10s infinite linear",
                animationPlayState: "paused",
                content: '""',
              },
              "&:hover::before": {
                background:
                  "linear-gradient(#000 0 0) content-box, conic-gradient(in hsl longer hue from var(--borderRotationAngle), rgb(255 0 0 / 0.5) 0 100%)",
                animationPlayState: "running",
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 400,
            fontSize: "1.2rem",
            gap: "4px",
            minWidth: "46px",
            "& .MuiButton-startIcon": {
              marginRight: 0,
              marginLeft: 0,
            },
          },
          contained: ({ theme, ownerState }) => {
            if (ownerState.disabled) {
              return {
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
              };
            }

            const color = ownerState.color || "primary";
            if (color === "inherit") {
              return {};
            }

            const paletteColor =
              theme.palette[color as keyof typeof theme.palette];
            const mainColor =
              (paletteColor as any)?.main || theme.palette.primary.main;
            const lightColor = lighten(mainColor, 0.1);
            const darkColor = darken(mainColor, 0.35);

            return {
              background: `linear-gradient(45deg, ${mainColor} 30%, ${lightColor} 90%)`,
              boxShadow: "none",
              transition: "background 3s ease",
              "&:hover": {
                background: `linear-gradient(65deg, ${mainColor} 30%, ${darkColor} 90%)`,
                boxShadow: "none",
                backgroundSize: "200% 200%",
                animation: "gradientShift 2s infinite",
              },
              "@keyframes gradientShift": {
                "0%": {
                  backgroundPosition: "0% 0%",
                },
                "50%": {
                  backgroundPosition: "100% 100%",
                },
                "100%": {
                  backgroundPosition: "0% 0%",
                },
              },
            };
          },
          sizeLarge: {
            paddingLeft: 32,
            paddingRight: 32,
            paddingTop: 16,
            paddingBottom: 16,
          },
        },
      },
    },
  });
}

// Extend the theme to include custom colors
declare module "@mui/material/styles" {
  interface Palette {
    neutral: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
    yoto: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
  }
  interface PaletteOptions {
    neutral?: {
      main?: string;
      light?: string;
      dark?: string;
      contrastText?: string;
    };
    yoto?: {
      main?: string;
      light?: string;
      dark?: string;
      contrastText?: string;
    };
  }
  interface TypeBackground {
  }
  interface PaletteBackgroundOptions {
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    gradient: true;
    snake: true;
    audio: true;
  }
  interface ButtonPropsColorOverrides {
    neutral: true;
    yoto: true;
  }
}

declare module "@mui/material/ButtonGroup" {
  interface ButtonGroupPropsVariantOverrides {
    audio: true;
  }
  interface ButtonGroupPropsColorOverrides {
    neutral: true;
    yoto: true;
  }
}

declare module "@mui/material/SvgIcon" {
  interface SvgIconPropsColorOverrides {
    neutral: true;
    yoto: true;
  }
}

declare module "@mui/material/IconButton" {
  interface IconButtonPropsColorOverrides {
    neutral: true;
    yoto: true;
  }
}

const theme = createAppTheme();

export default theme;
